import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { 
  Typography, TextField, Button, Box, Grid, CircularProgress, Card, CardContent, Stack, FormControlLabel, Switch
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { useSnackbar } from 'notistack';
import rutaService from '../../services/rutaService';

interface RutaFormData {
  codigoRuta: number;
  nombreRuta: string;
  sincronizar?: number;
}

const defaultForm: RutaFormData = {
  codigoRuta: 0,
  nombreRuta: '',
  sincronizar: 0
};

const RutaForm: React.FC = () => {
  const navigate = useNavigate();
  const { enqueueSnackbar } = useSnackbar();
  const { codigoRuta } = useParams();
  const [formData, setFormData] = useState<RutaFormData>({ ...defaultForm });
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  const loadRuta = useCallback(async () => {
    if (!codigoRuta) return;
    try {
      setLoading(true);
      const ruta = await rutaService.getRuta(Number(codigoRuta));
      setFormData({
        codigoRuta: ruta.codigoRuta || 0,
        nombreRuta: ruta.nombreRuta || '',
        sincronizar: ruta.sincronizar
      });
    } catch (error) {
      enqueueSnackbar('Error al cargar la ruta', { variant: 'error' });
      navigate('/ventas/rutas');
    } finally {
      setLoading(false);
    }
  }, [codigoRuta, enqueueSnackbar, navigate]);

  useEffect(() => {
    if (codigoRuta) loadRuta();
  }, [codigoRuta, loadRuta]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    if (name === 'codigoRuta') {
      setFormData(prev => ({ ...prev, [name]: Number(value) }));
    } else {
      setFormData(prev => ({ ...prev, [name]: value }));
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      setSaving(true);
      
      if (!formData.nombreRuta.trim()) {
        enqueueSnackbar('El nombre de la ruta es requerido', { variant: 'warning' });
        return;
      }

      if (codigoRuta) {
        await rutaService.updateRuta(Number(codigoRuta), formData);
      } else {
        await rutaService.createRuta(formData);
      }

      enqueueSnackbar('Ruta guardada exitosamente', { variant: 'success' });
      navigate('/ventas/rutas');
    } catch (error: any) {
      console.error('Error al guardar ruta:', error);
      enqueueSnackbar(error?.response?.data?.message || 'Error al guardar la ruta', { variant: 'error' });
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <Box sx={{ p: { xs: 2, sm: 3 }, bgcolor: '#f5f5f5', minHeight: '100vh', display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ p: { xs: 2, sm: 3 }, bgcolor: '#f5f5f5', minHeight: '100vh', maxWidth: '100%', overflowX: 'hidden' }}>
      <Card sx={{ bgcolor: '#ffffff', borderRadius: 3, boxShadow: '0 2px 8px rgba(0,0,0,0.1)', overflow: 'hidden' }}>
        <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
          <Stack
            direction={{ xs: 'column', sm: 'row' }}
            alignItems={{ xs: 'flex-start', sm: 'center' }}
            spacing={1.5}
            sx={{ mb: 3, flexWrap: 'wrap', gap: 1 }}
          >
            <Button
              startIcon={<ArrowBackIcon />}
              onClick={() => navigate('/ventas/rutas')}
              sx={{ textTransform: 'none' }}
            >
              Rutas
            </Button>
            <Typography variant="h4" sx={{ fontWeight: 600, color: '#1976d2' }}>
              {codigoRuta ? 'Editar Ruta' : 'Nueva Ruta'}
            </Typography>
          </Stack>
          <form onSubmit={handleSubmit}>
            <Grid container spacing={3}>
              <Grid item xs={12} md={3}>
                <TextField 
                  name="codigoRuta" 
                  label="Código" 
                  type="number"
                  value={formData.codigoRuta} 
                  onChange={handleChange} 
                  fullWidth 
                  required 
                  disabled={!!codigoRuta}
                  sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                />
              </Grid>
              <Grid item xs={12} md={9}>
                <TextField 
                  name="nombreRuta" 
                  label="Nombre de Ruta" 
                  value={formData.nombreRuta} 
                  onChange={handleChange} 
                  fullWidth 
                  required
                  sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                />
              </Grid>
              <Grid item xs={12}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={formData.sincronizar === 1}
                      onChange={(e) => setFormData(prev => ({ ...prev, sincronizar: e.target.checked ? 1 : 0 }))}
                    />
                  }
                  label="Sincronizar"
                />
              </Grid>
            </Grid>

            <Stack direction="row" spacing={2} sx={{ mt: 4 }}>
              <Button 
                type="submit" 
                variant="contained" 
                color="primary" 
                disabled={saving}
                sx={{ borderRadius: 2, px: 4, py: 1.5, textTransform: 'none', fontWeight: 500 }}
              >
                {saving ? <CircularProgress size={24} /> : 'Guardar'}
              </Button>
              <Button 
                variant="outlined" 
                onClick={() => navigate('/ventas/rutas')} 
                disabled={saving}
                sx={{ borderRadius: 2, px: 4, py: 1.5, textTransform: 'none', fontWeight: 500 }}
              >
                Cancelar
              </Button>
            </Stack>
          </form>
        </CardContent>
      </Card>
    </Box>
  );
};

export default RutaForm;
